/** Choose tours: how discovery rows are grouped, ordered and worded. Pure, so the ranking
 *  (the screen's focal moment) is unit-tested apart from the DOM. */
import type { EasyProduct } from '@/api/easyOnboarding';
import { formatTripDuration } from '@/features/records/time/tripDuration';

/** Row states the discovery task writes once a product has been probed (§5.6). */
const SETTLED = new Set(['done', 'no_bookings', 'error']);

export function isSettled(p: EasyProduct): boolean {
  return SETTLED.has(p.state);
}

export function isNoBookings(p: EasyProduct): boolean {
  return isSettled(p) && p.state !== 'error' && p.bookings_found === 0;
}

export interface ProductGroups {
  /** Settled rows with bookings (by bookings, descending), then failed rows, then rows still
   *  being probed (or checked again) in catalogue order. */
  main: EasyProduct[];
  /** Settled rows with nothing to import — the collapsed disclosure. */
  empty: EasyProduct[];
}

/** `checking`: rows a discovery re-run is checking again. They read as being checked (with
 *  the probing rows, below every settled row) until the run settles them anew. */
export function groupProducts(
  products: EasyProduct[],
  checking: ReadonlySet<number> = new Set(),
): ProductGroups {
  const found: EasyProduct[] = [];
  const failed: EasyProduct[] = [];
  const probing: EasyProduct[] = [];
  const empty: EasyProduct[] = [];
  for (const p of products) {
    if (!isSettled(p) || checking.has(p.pk)) probing.push(p);
    else if (p.state === 'error') failed.push(p);
    else if (p.bookings_found === 0) empty.push(p);
    else found.push(p);
  }
  // A stable sort: equal counts keep catalogue order.
  found.sort((a, b) => b.bookings_found - a.bookings_found);
  return { main: [...found, ...failed, ...probing], empty };
}

/** "8+" when the per-product sample stopped counting. */
export function bookingsLabel(p: EasyProduct): string {
  return p.capped ? `${p.bookings_found}+` : String(p.bookings_found);
}

/** "Daily · 2 h" / "Multi-day · 3 d" (the shared duration helper's words), or null. */
export function rhythmLabel(p: Pick<EasyProduct, 'cadence' | 'duration_min'>): string | null {
  const duration = p.duration_min != null ? formatTripDuration(p.duration_min * 60) : null;
  const kind = p.cadence === 'multi_day' ? 'Multi-day' : p.cadence === 'day' ? 'Daily' : null;
  if (kind && duration) return `${kind} · ${duration}`;
  return kind ?? duration;
}

/** "Sat 11 Oct, 10:00": the departure's own wall clock. FareHarbor sends the vendor's local
 *  time with its offset and no colon ("2026-10-13T10:00:00+0200"), so the date and time are
 *  read from the string itself — no Date parsing (whose handling of "+0200" varies) and no
 *  zone conversion. Anything else (UTC "Z") is parsed and shown in `timeZone`. */
export function departureLabel(iso: string | null, timeZone?: string | null): string | null {
  if (!iso) return null;
  const opts: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  };
  // Only a string carrying its own offset is a vendor wall clock; "Z" is UTC, converted below.
  const m =
    /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?[+-]\d{2}:?\d{2}$/.exec(iso);
  if (m) {
    const wall = new Date(Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!, +m[4]!, +m[5]!));
    if (!Number.isNaN(wall.getTime()))
      return new Intl.DateTimeFormat('en-GB', { ...opts, timeZone: 'UTC' }).format(wall);
  }
  const d = new Date(iso.replace(/([+-]\d{2})(\d{2})$/, '$1:$2'));
  if (Number.isNaN(d.getTime())) return null;
  try {
    return new Intl.DateTimeFormat('en-GB', { ...opts, timeZone: timeZone || undefined }).format(d);
  } catch {
    return new Intl.DateTimeFormat('en-GB', opts).format(d);
  }
}

/** Sample bookings the ticked products will bring (their found counts). */
export function sampleBookingCount(products: EasyProduct[], selected: ReadonlySet<number>): number {
  return products.reduce((n, p) => (selected.has(p.pk) ? n + p.bookings_found : n), 0);
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

let regionNames: Intl.DisplayNames | null | undefined;
/** "France" for "FR" (ISO-3166 alpha-2); the code itself when the browser cannot name it. */
export function countryName(code: string | null | undefined): string {
  if (!code) return '';
  if (regionNames === undefined) {
    try {
      regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
    } catch {
      regionNames = null;
    }
  }
  try {
    return regionNames?.of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
}

let languageDisplay: Intl.DisplayNames | null | undefined;
/** "English, Italian" for ["en", "it"]; a code the browser cannot name stays a code. */
export function languageNames(codes: readonly string[]): string {
  if (languageDisplay === undefined) {
    try {
      languageDisplay = new Intl.DisplayNames(['en'], { type: 'language' });
    } catch {
      languageDisplay = null;
    }
  }
  return codes
    .map((c) => {
      try {
        return languageDisplay?.of(c) ?? c;
      } catch {
        return c;
      }
    })
    .join(', ');
}

function zonedParts(
  d: Date,
  opts: Intl.DateTimeFormatOptions,
  timeZone: string | null | undefined,
): (t: Intl.DateTimeFormatPartTypes) => string {
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat('en-GB', { ...opts, timeZone: timeZone || undefined }).formatToParts(d);
  } catch {
    parts = new Intl.DateTimeFormat('en-GB', opts).formatToParts(d);
  }
  return (t) => parts.find((p) => p.type === t)?.value ?? '';
}

/** "18:18:01": a log line's instant as a wall clock in the tenant's zone. Anything that is
 *  not a timestamp comes back as it was. */
export function clockLabel(ts: string, timeZone?: string | null): string {
  // The log tail writes "2026-10-10 16:18:01,123" (UTC, no zone) or ISO with a zone.
  const iso = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(ts) && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(ts)
    ? `${ts.replace(' ', 'T').replace(',', '.')}Z`
    : ts;
  const d = new Date(iso);
  if (!ts || Number.isNaN(d.getTime())) return ts;
  const v = zonedParts(
    d,
    { hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' },
    timeZone,
  );
  return `${v('hour')}:${v('minute')}:${v('second')}`;
}

/** "Mon, 12 Oct, 10:00": a server timestamp (an instant) in the tenant's zone — how the
 *  surface writes a moment (the logs, the task tabs). Null for a missing or bad value. */
export function instantLabel(
  iso: string | null | undefined,
  timeZone?: string | null,
  { short = false }: { short?: boolean } = {},
): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const v = zonedParts(
    d,
    {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    },
    timeZone,
  );
  // Short (a phone's tab label): "10 Oct 18:18".
  if (short) return `${v('day')} ${v('month')} ${v('hour')}:${v('minute')}`;
  return `${v('weekday')}, ${v('day')} ${v('month')}, ${v('hour')}:${v('minute')}`;
}

/** A shared prefix shorter than this is no reason to split a name. */
const SHARED_PREFIX_MIN = 16;

export interface NameParts {
  /** The name as one line would show it (the shared prefix when split). */
  head: string;
  /** The part that tells it apart from its siblings ("Semi Private (English)"), or null. */
  tail: string | null;
}

/** Splits each name whose long prefix it shares with a sibling, at a word boundary, so two
 *  rows that truncate alike still read apart: "Venice Gondola Ride and Serenade" over
 *  "Semi Private (English)". Separators between the parts (" - ", " – ", ":", ",") drop. */
export function siblingNameParts(names: readonly string[]): Map<string, NameParts> {
  const out = new Map<string, NameParts>();
  const unique = [...new Set(names)];
  for (const name of unique) {
    let best = 0;
    for (const other of unique) {
      if (other === name) continue;
      let i = 0;
      while (i < name.length && i < other.length && name[i] === other[i]) i += 1;
      // Back off to a word boundary inside the name.
      if (i < name.length) {
        const cut = name.lastIndexOf(' ', i);
        i = cut > 0 ? cut : 0;
      }
      best = Math.max(best, i);
    }
    const head = name.slice(0, best).replace(/[\s\-–—:,(]+$/, '');
    const tail = name.slice(best).replace(/^[\s\-–—:,]+/, '');
    out.set(
      name,
      head.length >= SHARED_PREFIX_MIN && tail ? { head, tail } : { head: name, tail: null },
    );
  }
  return out;
}

/** What a discovery row says once checked: a re-run that leaves it exactly so has not been
 *  seen to settle it again yet. */
export function rowSignature(p: EasyProduct): string {
  return [p.state, p.bookings_found, p.capped, p.next_departure, p.cadence, p.duration_min].join('|');
}

/** The rows a discovery re-run is still checking: those it found settled when it started
 *  (`snapshot`) and has not changed since. */
export function rowsBeingRechecked(
  products: readonly EasyProduct[],
  snapshot: ReadonlyMap<number, string> | null,
): Set<number> {
  const out = new Set<number>();
  if (!snapshot) return out;
  for (const p of products) if (snapshot.get(p.pk) === rowSignature(p)) out.add(p.pk);
  return out;
}
